import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Inbox, FileText, Settings2, LogOut, Plus, Pencil, Trash2, X, RefreshCw,
} from "lucide-react";
import { SEO } from "../../components/SEO";
import { api } from "../../lib/api";
import { BLOG_CATEGORIES, FIRM } from "../../data/content";

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold transition";

const emptyArticle = { title: "", category: BLOG_CATEGORIES[0], excerpt: "", content: "", image_url: "", published: true };

function EnquiriesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/enquiries").then((r) => setItems(r.data)).catch(() => toast.error("Could not load enquiries.")).finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setItems((arr) => arr.map((e) => (e.enquiry_id === id ? { ...e, status } : e)));
      toast.success("Status updated.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setItems((arr) => arr.filter((e) => e.enquiry_id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (loading) return <p className="text-sm text-slate-500 py-10 text-center">Loading enquiries…</p>;
  if (items.length === 0) return <p className="text-sm text-slate-500 py-10 text-center" data-testid="enquiries-empty">No enquiries yet.</p>;

  return (
    <div className="space-y-4" data-testid="enquiries-list">
      {items.map((e) => (
        <div key={e.enquiry_id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`enquiry-item-${e.enquiry_id}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-semibold text-navy">{e.name} <span className="ml-2 text-xs font-normal text-slate-400">{e.client_type}</span></p>
              <p className="mt-1 text-sm text-slate-600">{e.mobile} · {e.email} · {e.city}</p>
              <p className="mt-1 text-xs text-slate-400">
                {e.created_at ? format(new Date(e.created_at), "dd MMM yyyy, hh:mm a") : ""} · Prefers {e.contact_method}
                {e.preferred_date ? ` · Consultation: ${e.preferred_date}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={e.status}
                onChange={(ev) => setStatus(e.enquiry_id, ev.target.value)}
                data-testid={`enquiry-status-${e.enquiry_id}`}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${
                  e.status === "new" ? "bg-amber-50 border-amber-300 text-amber-700"
                  : e.status === "contacted" ? "bg-blue-50 border-blue-300 text-blue-700"
                  : "bg-emerald-50 border-emerald-300 text-emerald-700"
                }`}
              >
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="closed">Closed</option>
              </select>
              <button onClick={() => remove(e.enquiry_id)} data-testid={`enquiry-delete-${e.enquiry_id}`} className="p-2 text-slate-400 hover:text-red-600 transition-colors" aria-label="Delete enquiry">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
          <div className="mt-4 rounded-lg bg-slate-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-gold">{e.service}</p>
            {e.message && <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{e.message}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ArticlesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyArticle);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/articles").then((r) => setItems(r.data)).catch(() => toast.error("Could not load articles."));
  }, []);

  useEffect(load, [load]);

  const openEditor = (article) => {
    setEditing(article || null);
    setForm(article ? { title: article.title, category: article.category, excerpt: article.excerpt, content: article.content, image_url: article.image_url || "", published: article.published } : emptyArticle);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { _open, ...payload } = form;
    try {
      if (editing) {
        await api.put(`/admin/articles/${editing.article_id}`, payload);
        toast.success("Article updated.");
      } else {
        await api.post("/admin/articles", payload);
        toast.success("Article published.");
      }
      setEditing(null);
      setForm(emptyArticle);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article permanently?")) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      setItems((arr) => arr.filter((a) => a.article_id !== id));
      toast.success("Article deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div data-testid="articles-tab">
      {form._open || editing ? (
        <form onSubmit={save} className="rounded-xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5" data-testid="article-editor-form">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-xl font-semibold text-navy">{editing ? "Edit Article" : "New Article"}</h3>
            <button type="button" onClick={() => { setEditing(null); setForm(emptyArticle); }} data-testid="article-editor-cancel" className="p-2 text-slate-400 hover:text-navy"><X size={18} /></button>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Title *</label>
            <input required className={inputCls} value={form.title} onChange={set("title")} data-testid="article-title-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Category *</label>
              <select className={inputCls} value={form.category} onChange={set("category")} data-testid="article-category-select">
                {BLOG_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Featured Image URL</label>
              <input className={inputCls} value={form.image_url} onChange={set("image_url")} placeholder="https://…" data-testid="article-image-input" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Brief Description *</label>
            <textarea required rows={2} className={inputCls} value={form.excerpt} onChange={set("excerpt")} data-testid="article-excerpt-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Full Article * (blank line between paragraphs)</label>
            <textarea required rows={10} className={inputCls} value={form.content} onChange={set("content")} data-testid="article-content-input" />
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={set("published")} className="w-4 h-4 accent-[#C5A059]" data-testid="article-published-checkbox" />
            <span className="text-sm text-slate-600">Published (visible on the website)</span>
          </label>
          <button type="submit" disabled={saving} data-testid="article-save-button" className="rounded-full bg-navy px-7 py-3 text-sm font-semibold text-white hover:bg-navy-light disabled:opacity-60 transition-colors">
            {saving ? "Saving…" : editing ? "Update Article" : "Publish Article"}
          </button>
        </form>
      ) : (
        <>
          <div className="flex justify-end mb-5">
            <button
              onClick={() => setForm({ ...emptyArticle, _open: true })}
              data-testid="new-article-button"
              className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-light transition-colors"
            >
              <Plus size={15} /> New Article
            </button>
          </div>
          {items.length === 0 ? (
            <p className="text-sm text-slate-500 py-10 text-center" data-testid="articles-empty">No articles yet.</p>
          ) : (
            <div className="space-y-3" data-testid="articles-list">
              {items.map((a) => (
                <div key={a.article_id} className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4" data-testid={`article-row-${a.slug}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-navy truncate">{a.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {a.category} · {a.created_at ? format(new Date(a.created_at), "dd MMM yyyy") : ""} ·{" "}
                      <span className={a.published ? "text-emerald-600 font-semibold" : "text-amber-600 font-semibold"}>{a.published ? "Published" : "Draft"}</span>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEditor(a)} data-testid={`article-edit-${a.slug}`} className="p-2 rounded-lg text-slate-400 hover:text-navy hover:bg-slate-100 transition-colors" aria-label="Edit article"><Pencil size={16} /></button>
                    <button onClick={() => remove(a.article_id)} data-testid={`article-delete-${a.slug}`} className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" aria-label="Delete article"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SettingsTab() {
  const [hours, setHours] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setHours(r.data.business_hours)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { business_hours: hours });
      toast.success("Business hours updated.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="settings-tab-content">
      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Business Hours (shown on the Contact page)</label>
      <textarea rows={4} className={inputCls} value={hours} onChange={(e) => setHours(e.target.value)} data-testid="business-hours-input" />
      <button onClick={save} disabled={saving} data-testid="settings-save-button" className="mt-5 rounded-full bg-navy px-7 py-3 text-sm font-semibold text-white hover:bg-navy-light disabled:opacity-60 transition-colors">
        {saving ? "Saving…" : "Save Changes"}
      </button>
    </div>
  );
}

export default function AdminDashboard({ user }) {
  const [tab, setTab] = useState("enquiries");
  const navigate = useNavigate();

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    navigate("/admin/login", { replace: true });
  };

  const tabs = [
    { id: "enquiries", label: "Enquiries", icon: Inbox },
    { id: "articles", label: "Articles", icon: FileText },
    { id: "settings", label: "Site Settings", icon: Settings2 },
  ];

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <SEO title="Admin Dashboard" description="Administration area." path="/admin" />
      <header className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-serif text-xl font-bold">{FIRM.name}</p>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-400 mt-0.5">Administration · {user?.email}</p>
          </div>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-xs font-semibold hover:bg-white/10 transition-colors">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-2 mb-8 flex-wrap" data-testid="admin-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                tab === t.id ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-gold"
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>
        {tab === "enquiries" && <EnquiriesTab />}
        {tab === "articles" && <ArticlesTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}
